// src/pages/IndependentAgentsTab.tsx
// Independent AI Agent Fleet — all 13 agents, wave roadmap, knowledge register
// Works entirely on mock data — no Neon or Cloudflare required.

import { useState } from 'react'
import {
  ALL_AGENTS, WAVE_META, getActiveAgents,
  AgentProfile, AgentWave,
} from '../utils/mockDataAgents'
import {
  Bot, Shield, CheckCircle, Clock, BookOpen, Zap, ChevronRight,
  X, Activity, Star, AlertTriangle, Info, Layers,
} from 'lucide-react'

// ── Module labels ─────────────────────────────────────────────────

const MODULE_LABELS: Record<string, string> = {
  'swms': 'SWMS', 'incidents': 'Incidents', 'permits': 'Permits to Work',
  'toolbox': 'Toolbox & Alerts', 'whs-plan': 'WHS Management Plan',
  'whs-compliance': 'Compliance Register', 'sds-register': 'SDS Register',
  'itp': 'ITP & Hold Points', 'works-instr': 'Work Instructions',
  'drawings': 'Drawings & Specs', 'handover': 'Handover', 'qa-ncr': 'QA / NCR',
  'contracts': 'Contracts', 'variations': 'Variations', 'live-budget': 'Live Budget',
  'claims': 'Progress Claims', 'subcontracts': 'Subcontracts', 'pos': 'Purchase Orders',
  'jobs': 'Jobs Register', 'reports': 'Reports', 'estimating': 'Estimating',
  'rfq': 'RFQ Portal', 'suppliers': 'Suppliers', 'catalogue': 'Catalogue',
  'spend-analytics': 'Spend Analytics', 'xero': 'Xero Integration',
  'timesheets': 'Timesheets', 'hv-switching': 'HV Switching',
  'its-comms': 'ITS Commissioning', 'mobile': 'Mobile & Crew',
  'environmental': 'Environmental', 'site-diary': 'Site Diary',
}

// ── Agent detail panel ────────────────────────────────────────────

function AgentDetail({ agent, onClose }: { agent: AgentProfile; onClose: () => void }) {
  const wave = WAVE_META[agent.wave]
  const isActive = agent.status === 'ACTIVE'

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b bg-[#1F3864] flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-blue-300">{agent.agentId}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${wave.bgColor} ${wave.textColor}`}>
                {wave.label}
              </span>
              {isActive && (
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Active
                </span>
              )}
            </div>
            <div className="font-semibold text-white text-base">{agent.name}</div>
            <div className="text-xs text-blue-300 mt-0.5">{agent.aqfLevel} · Autonomy Level {agent.autonomyLevel} — {['Advisory','Assisted','Automated'][agent.autonomyLevel - 1]}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-blue-300 hover:text-white hover:bg-white/10 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Description */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Role & function</div>
          <p className="text-sm text-gray-700 leading-relaxed">{agent.description}</p>
        </div>

        {/* Capabilities */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Core capabilities</div>
          <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-700 leading-relaxed">
            {agent.capabilities}
          </div>
        </div>

        {/* Knowledge authorities */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Primary knowledge authorities</div>
          <div className="space-y-1.5">
            {agent.primaryKnowledge.map((k, i) => (
              <div key={i} className="flex items-start gap-2.5 text-xs text-gray-700">
                <BookOpen className="w-3.5 h-3.5 text-[#2E75B6] flex-shrink-0 mt-0.5" />
                {k}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
            <span>Version: <span className="font-mono text-gray-600">{agent.knowledgeVersion}</span></span>
            <span>·</span>
            <span>Currency: <span className={`font-medium ${agent.knowledgeCurrency >= 90 ? 'text-green-600' : 'text-amber-600'}`}>{agent.knowledgeCurrency}%</span></span>
            <span>·</span>
            <span>Next review: {new Date(agent.nextReviewDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>

        {/* Platform modules */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Platform modules</div>
          <div className="flex flex-wrap gap-1.5">
            {agent.primaryModules.map(m => (
              <span key={m} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">
                {MODULE_LABELS[m] ?? m}
              </span>
            ))}
          </div>
        </div>

        {/* Trigger events */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Trigger events</div>
          <div className="flex flex-wrap gap-1.5">
            {agent.triggerEvents.map(e => (
              <span key={e} className="text-xs bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded font-mono">
                {e}
              </span>
            ))}
          </div>
        </div>

        {/* Status notice */}
        {!isActive && (
          <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-xs font-semibold text-amber-800 mb-0.5">Deployment scheduled: {WAVE_META[agent.wave].target}</div>
              <div className="text-xs text-amber-700">
                This agent is registered in the platform and visible in the agent fleet. Knowledge base seeding and activation will occur when Wave {agent.wave} infrastructure is ready.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Agent card ────────────────────────────────────────────────────

function AgentCard({
  agent,
  selected,
  onClick,
}: {
  agent: AgentProfile
  selected: boolean
  onClick: () => void
}) {
  const wave = WAVE_META[agent.wave]
  const isActive = agent.status === 'ACTIVE'

  return (
    <div
      onClick={onClick}
      className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
        selected
          ? 'border-[#2E75B6] ring-2 ring-blue-100'
          : isActive
          ? 'border-green-200 hover:border-green-300'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isActive ? 'bg-[#1F3864]' : 'bg-gray-100'
          }`}>
            <Bot className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
          </div>
          <div>
            <div className="text-xs font-mono text-gray-400">{agent.agentId}</div>
            <div className="text-sm font-semibold text-gray-900 leading-snug">{agent.shortName}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${wave.bgColor} ${wave.textColor}`}>
            {wave.label}
          </span>
          {isActive && (
            <span className="flex items-center gap-1 text-[10px] text-green-600 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Active
            </span>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-2.5 line-clamp-2 leading-relaxed">{agent.description}</div>

      <div className="flex items-center justify-between text-[10px]">
        <span className="text-gray-400">{agent.aqfLevel}</span>
        <div className="flex items-center gap-1 text-gray-400">
          <Activity className="w-3 h-3" />
          <span>{agent.knowledgeCurrency}% current</span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {agent.primaryModules.slice(0, 3).map(m => (
          <span key={m} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
            {MODULE_LABELS[m] ?? m}
          </span>
        ))}
        {agent.primaryModules.length > 3 && (
          <span className="text-[10px] text-gray-400">+{agent.primaryModules.length - 3} more</span>
        )}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────

export function IndependentAgentsTab() {
  const [selected, setSelected] = useState<AgentProfile | null>(null)
  const [waveFilter, setWaveFilter] = useState<AgentWave | 'ALL'>('ALL')

  const waves: AgentWave[] = [1, 2, 3, 4, 5]
  const activeAgents = getActiveAgents()

  const filtered = waveFilter === 'ALL'
    ? ALL_AGENTS
    : ALL_AGENTS.filter(a => a.wave === waveFilter)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-[#2E75B6]" />
            Independent AI Agents
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            13 specialist agents · Wave 1 active · Full deployment Q2 2027
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
          <Info className="w-3.5 h-3.5 text-[#2E75B6]" />
          All outputs are advisory — licensed professionals retain full responsibility
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total agents', value: ALL_AGENTS.length, color: 'text-gray-900', sub: 'across 5 waves' },
          { label: 'Active now', value: activeAgents.length, color: 'text-green-700', sub: 'Wave 1 — Q3 2026' },
          { label: 'Platform modules', value: 22, color: 'text-[#2E75B6]', sub: 'covered by agents' },
          { label: 'Full deployment', value: 'Q2 2027', color: 'text-gray-700', sub: 'Wave 5 complete', isText: true },
        ].map(({ label, value, color, sub, isText }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`${isText ? 'text-xl' : 'text-2xl'} font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* Active agents highlight */}
      <div className="bg-[#1F3864] rounded-xl px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-white">Wave 1 — Active agents</span>
          <span className="text-xs text-blue-300 ml-1">Processing platform events now</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {activeAgents.map(a => (
            <div
              key={a.agentId}
              onClick={() => setSelected(a)}
              className="bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-mono text-blue-300">{a.agentId}</span>
                <span className="flex items-center gap-1 text-[10px] text-green-400 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live
                </span>
              </div>
              <div className="text-sm font-semibold text-white">{a.name}</div>
              <div className="text-xs text-blue-300 mt-0.5">{a.aqfLevel} · {a.primaryModules.length} modules</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {a.primaryModules.slice(0, 4).map(m => (
                  <span key={m} className="text-[10px] bg-white/10 text-blue-200 px-1.5 py-0.5 rounded">
                    {MODULE_LABELS[m] ?? m}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Professional responsibility notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3 text-sm">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800">
          <span className="font-semibold">Professional responsibility boundary: </span>
          All agent outputs are advisory only. Licensed professionals (WHSO, RPEQ, solicitors, CPAs) retain full professional responsibility. Agents cannot issue statutory notices, sign certifications, or represent the company in legal matters.
        </div>
      </div>

      {/* Wave filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setWaveFilter('ALL')}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
            waveFilter === 'ALL' ? 'bg-[#1F3864] text-white border-[#1F3864]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
          }`}
        >
          All waves ({ALL_AGENTS.length})
        </button>
        {waves.map(w => {
          const meta = WAVE_META[w]
          const count = ALL_AGENTS.filter(a => a.wave === w).length
          return (
            <button
              key={w}
              onClick={() => setWaveFilter(w)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                waveFilter === w
                  ? `${meta.bgColor} ${meta.textColor} ${meta.borderColor}`
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              {meta.label} · {count} agent{count !== 1 ? 's' : ''} · {meta.target.split(' — ')[1]}
            </button>
          )
        })}
      </div>

      {/* Agent grid + detail panel */}
      <div className="flex gap-5 items-start">
        <div className={`${selected ? 'w-1/2' : 'w-full'} grid ${selected ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'} gap-3 transition-all`}>
          {filtered.map(agent => (
            <AgentCard
              key={agent.agentId}
              agent={agent}
              selected={selected?.agentId === agent.agentId}
              onClick={() => setSelected(selected?.agentId === agent.agentId ? null : agent)}
            />
          ))}
        </div>

        {selected && (
          <div className="w-1/2 sticky top-6" style={{ maxHeight: 'calc(100vh - 160px)' }}>
            <AgentDetail agent={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>

      {/* Wave deployment roadmap */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-4 h-4 text-[#2E75B6]" />
          <h3 className="font-semibold text-gray-900 text-sm">Wave deployment roadmap</h3>
        </div>
        <div className="space-y-3">
          {waves.map(w => {
            const meta = WAVE_META[w]
            const agents = ALL_AGENTS.filter(a => a.wave === w)
            return (
              <div key={w} className={`border rounded-xl px-4 py-3 ${meta.borderColor} ${meta.bgColor}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${meta.dotColor}`} />
                    <span className={`text-xs font-semibold ${meta.textColor}`}>{meta.label} — {meta.target}</span>
                  </div>
                  {w === 1 && (
                    <span className="text-[10px] bg-green-500 text-white px-2 py-0.5 rounded-full font-medium">
                      Deployed
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {agents.map(a => (
                    <button
                      key={a.agentId}
                      onClick={() => setSelected(a)}
                      className={`text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-colors bg-white ${meta.borderColor} hover:shadow-sm`}
                    >
                      <span className="font-mono text-gray-400">{a.agentId}</span>
                      <span className="text-gray-700 font-medium">{a.shortName}</span>
                      <ChevronRight className="w-3 h-3 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Knowledge register summary */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-4 h-4 text-[#2E75B6]" />
          <h3 className="font-semibold text-gray-900 text-sm">Knowledge version register</h3>
          <span className="text-xs text-gray-400 ml-1">· Active agents only</span>
        </div>
        <div className="space-y-2">
          {activeAgents.map(a => (
            <div key={a.agentId} className="flex items-center gap-4 py-2 border-b border-gray-100 last:border-0">
              <span className="text-xs font-mono text-gray-400 w-8">{a.agentId}</span>
              <span className="text-sm font-medium text-gray-900 flex-1">{a.shortName}</span>
              <span className="text-xs font-mono text-gray-600">{a.knowledgeVersion}</span>
              <div className="flex items-center gap-1.5">
                <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${a.knowledgeCurrency >= 90 ? 'bg-green-500' : 'bg-amber-400'}`}
                    style={{ width: `${a.knowledgeCurrency}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${a.knowledgeCurrency >= 90 ? 'text-green-600' : 'text-amber-600'}`}>
                  {a.knowledgeCurrency}%
                </span>
              </div>
              <span className="text-xs text-gray-400">
                Review {new Date(a.nextReviewDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}
              </span>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
